import React from 'react'
import { Text, LoadingSpinner, Button } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { CentralAmparoPageHeader } from '../../components/centralAmparo/CentralAmparoPageHeader'
import { CheckboxMultiSelect } from '../Qualidade/CheckboxMultiSelect'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { modulesService } from '../../services/modules'
import { supportCenterSpecialtiesService } from '../../services/supportCenterSpecialties'
import { supportCenterAppointmentsService } from '../../services/supportCenterAppointments'
import { supportCenterReportsService } from '../../services/supportCenterReports'
import { supportCenterTriageCatalogService } from '../../services/supportCenterTriageCatalog'
import type { SupportCenterSpecialization } from '../../types/supportCenterSpecialties'
import type { SupportCenterAttendantOption } from '../../types/supportCenterAppointments'
import type {
  SupportCenterDashboardFilters,
  SupportCenterDashboardTab,
  SupportCenterTriageStatusFilter,
} from '../../types/supportCenterReports'
import type {
  SupportCenterAppointmentStatus,
  SupportCenterDemandType,
  SupportCenterReferralOrigin,
} from '../../types/supportCenterTriage'
import {
  SUPPORT_CENTER_APPOINTMENT_CHANNEL_FILTER_OPTIONS,
  type SupportCenterAppointmentChannel,
} from '../../types/supportCenterAppointments'
import {
  DASHBOARD_INPUT_CLASS,
  daysAgoIso,
  getErrorMessage,
  todayIso,
} from '../../components/centralAmparo/dashboardHelpers'
import {
  STATUS_FILTER_OPTIONS,
  TRIAGE_STATUS_FILTER_OPTIONS,
} from '../../components/centralAmparo/calendarioAgendaHelpers'
import DashboardVolumetriaTab from './DashboardVolumetriaTab'
import DashboardHistoricoTab from './DashboardHistoricoTab'

const MODULE_NAME = 'central_amparo_dashboard'
const DEFAULT_MODULE_TITLE = 'Dashboard'

function defaultDashboardFilters(): SupportCenterDashboardFilters {
  return {
    dateFrom: daysAgoIso(30),
    dateTo: todayIso(),
    specializationIds: [],
    professionalUserIds: [],
    appointmentStatuses: [],
    triageStatuses: [],
    channels: [],
    patientQuery: '',
    policyNumbers: [],
    convenioCodes: [],
    assistanceIds: [],
    referralOriginIds: [],
    demandTypeIds: [],
  }
}

const CentralAmparoDashboard: React.FC = () => {
  const { user, profile } = useAuthContext()
  const actorId = user?.id || profile?.id || null
  const { hasPermission: canViewValues, loading: loadingViewValues } = usePermission(
    MODULE_NAME,
    'view_values'
  )
  const [activeTab, setActiveTab] = React.useState<SupportCenterDashboardTab>('volumetria')
  const [moduleInfo, setModuleInfo] = React.useState<{
    display_name: string
    description: string | null
  } | null>(null)
  const [specializations, setSpecializations] = React.useState<SupportCenterSpecialization[]>([])
  const [userSpecializationIds, setUserSpecializationIds] = React.useState<string[]>([])
  const [professionals, setProfessionals] = React.useState<SupportCenterAttendantOption[]>([])
  const [referralOrigins, setReferralOrigins] = React.useState<SupportCenterReferralOrigin[]>([])
  const [demandTypes, setDemandTypes] = React.useState<SupportCenterDemandType[]>([])
  const [catalogReady, setCatalogReady] = React.useState(false)
  const [catalogError, setCatalogError] = React.useState<string | null>(null)
  const [codesError, setCodesError] = React.useState<string | null>(null)
  const [dateFrom, setDateFrom] = React.useState(() => daysAgoIso(30))
  const [dateTo, setDateTo] = React.useState(() => todayIso())
  const [specializationIds, setSpecializationIds] = React.useState<string[]>([])
  const [professionalUserIds, setProfessionalUserIds] = React.useState<string[]>([])
  const [appointmentStatuses, setAppointmentStatuses] = React.useState<
    SupportCenterAppointmentStatus[]
  >([])
  const [triageStatuses, setTriageStatuses] = React.useState<SupportCenterTriageStatusFilter[]>(
    []
  )
  const [channels, setChannels] = React.useState<SupportCenterAppointmentChannel[]>([])
  const [patientQuery, setPatientQuery] = React.useState('')
  const [policyNumbers, setPolicyNumbers] = React.useState<string[]>([])
  const [convenioCodes, setConvenioCodes] = React.useState<string[]>([])
  const [policyOptions, setPolicyOptions] = React.useState<{ value: string; label: string }[]>([])
  const [convenioOptions, setConvenioOptions] = React.useState<{ value: string; label: string }[]>(
    []
  )
  const [assistanceIds, setAssistanceIds] = React.useState<string[]>([])
  const [referralOriginIds, setReferralOriginIds] = React.useState<string[]>([])
  const [demandTypeIds, setDemandTypeIds] = React.useState<string[]>([])
  const [appliedFilters, setAppliedFilters] = React.useState<SupportCenterDashboardFilters>(
    defaultDashboardFilters
  )
  const [refreshToken, setRefreshToken] = React.useState(0)
  const [filtersLoading, setFiltersLoading] = React.useState(false)

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName(MODULE_NAME)
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name || DEFAULT_MODULE_TITLE,
          description: result.data.description || null,
        })
      }
    }
    void loadModule()
  }, [])

  React.useEffect(() => {
    if (loadingViewValues) return
    const loadCatalog = async () => {
      setCatalogError(null)
      setCatalogReady(false)
      try {
        if (!canViewValues && !actorId) {
          setSpecializations([])
          setUserSpecializationIds([])
          setReferralOrigins([])
          setDemandTypes([])
          setCatalogError(
            'Não foi possível identificar o usuário logado para filtrar o dashboard.'
          )
          return
        }

        const [allSpecs, origins, demands, ownSpecIds] = await Promise.all([
          supportCenterSpecialtiesService.listSpecializations(),
          supportCenterTriageCatalogService.listReferralOrigins(),
          supportCenterTriageCatalogService.listDemandTypes(),
          canViewValues
            ? Promise.resolve([] as string[])
            : supportCenterSpecialtiesService.listSpecializationIdsForUser(actorId as string),
        ])

        if (canViewValues) {
          setSpecializations(allSpecs)
          setUserSpecializationIds([])
        } else {
          const allowed = new Set(ownSpecIds)
          setSpecializations(allSpecs.filter((spec) => allowed.has(spec.id)))
          setUserSpecializationIds(ownSpecIds)
          setSpecializationIds(ownSpecIds.length === 1 ? ownSpecIds : [])
          setProfessionalUserIds(actorId ? [actorId] : [])
          setAppliedFilters((prev) => ({
            ...prev,
            specializationIds: ownSpecIds,
            professionalUserIds: actorId ? [actorId] : [],
          }))
        }

        setReferralOrigins(origins)
        setDemandTypes(demands)
      } catch (error) {
        setSpecializations([])
        setUserSpecializationIds([])
        setReferralOrigins([])
        setDemandTypes([])
        setCatalogError(getErrorMessage(error))
      } finally {
        setCatalogReady(true)
      }
    }
    void loadCatalog()
  }, [actorId, canViewValues, loadingViewValues])

  React.useEffect(() => {
    let cancelled = false
    const loadCodes = async () => {
      setCodesError(null)
      try {
        const options = await supportCenterReportsService.listPolicyAndConvenioOptions()
        if (cancelled) return
        setPolicyOptions(options.policyNumbers.map((value) => ({ value, label: value })))
        setConvenioOptions(options.convenioCodes.map((value) => ({ value, label: value })))
        setPolicyNumbers((prev) => prev.filter((value) => options.policyNumbers.includes(value)))
        setConvenioCodes((prev) => prev.filter((value) => options.convenioCodes.includes(value)))
      } catch (error) {
        if (cancelled) return
        setPolicyOptions([])
        setConvenioOptions([])
        setPolicyNumbers([])
        setConvenioCodes([])
        setCodesError(getErrorMessage(error))
      }
    }
    void loadCodes()
    return () => {
      cancelled = true
    }
  }, [])

  React.useEffect(() => {
    if (loadingViewValues || !canViewValues) {
      setProfessionals([])
      return
    }
    let cancelled = false
    const loadProfessionals = async () => {
      try {
        const lists =
          specializationIds.length === 0
            ? [await supportCenterAppointmentsService.listAttendantsForAgenda()]
            : await Promise.all(
                specializationIds.map((id) =>
                  supportCenterAppointmentsService.listAttendantsForAgenda(id)
                )
              )
        const byId = new Map<string, SupportCenterAttendantOption>()
        for (const list of lists) {
          for (const professional of list) {
            byId.set(professional.id, professional)
          }
        }
        const merged = [...byId.values()].sort((a, b) =>
          a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
        )
        if (cancelled) return
        setProfessionals(merged)
        setProfessionalUserIds((prev) => prev.filter((id) => byId.has(id)))
      } catch {
        if (!cancelled) setProfessionals([])
      }
    }
    void loadProfessionals()
    return () => {
      cancelled = true
    }
  }, [specializationIds, canViewValues, loadingViewValues])

  const coverageOptions = React.useMemo(() => {
    const source =
      specializationIds.length === 0
        ? specializations
        : specializations.filter((spec) => specializationIds.includes(spec.id))
    const byId = new Map<string, { value: string; label: string }>()
    for (const spec of source) {
      for (const assistance of spec.assistances ?? []) {
        if (!assistance.id || byId.has(assistance.id)) continue
        const name = assistance.name.trim()
        const code = assistance.code.trim()
        const label = name && code && name !== code ? `${name} (${code})` : name || code
        byId.set(assistance.id, { value: assistance.id, label })
      }
    }
    return [...byId.values()].sort((a, b) =>
      a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' })
    )
  }, [specializations, specializationIds])

  React.useEffect(() => {
    const allowed = new Set(coverageOptions.map((option) => option.value))
    setAssistanceIds((prev) => prev.filter((id) => allowed.has(id)))
  }, [coverageOptions])

  const scopedProfessionalIds = React.useMemo(() => {
    if (canViewValues) return professionalUserIds
    return actorId ? [actorId] : []
  }, [canViewValues, professionalUserIds, actorId])

  const scopedSpecializationIds = React.useMemo(() => {
    if (canViewValues) return specializationIds
    if (userSpecializationIds.length <= 1) return userSpecializationIds
    if (specializationIds.length === 0) return userSpecializationIds
    return specializationIds.filter((id) => userSpecializationIds.includes(id))
  }, [canViewValues, specializationIds, userSpecializationIds])

  const canFilterProfessional = canViewValues
  const canFilterSpecialization = canViewValues || userSpecializationIds.length > 1
  const lockedSpecializationLabel =
    specializations.length === 1
      ? specializations[0].name
      : 'Nenhuma especialidade vinculada'

  const applyCurrentFilters = () => {
    setAppliedFilters({
      dateFrom,
      dateTo,
      specializationIds: scopedSpecializationIds,
      professionalUserIds: scopedProfessionalIds,
      appointmentStatuses,
      triageStatuses,
      channels,
      patientQuery,
      policyNumbers,
      convenioCodes,
      assistanceIds,
      referralOriginIds,
      demandTypeIds,
    })
    setRefreshToken((token) => token + 1)
  }

  const handleClearFilters = () => {
    const next = defaultDashboardFilters()
    if (!canViewValues) {
      next.specializationIds = userSpecializationIds
      next.professionalUserIds = actorId ? [actorId] : []
    }
    setDateFrom(next.dateFrom)
    setDateTo(next.dateTo)
    setSpecializationIds(canViewValues || userSpecializationIds.length !== 1 ? [] : userSpecializationIds)
    setProfessionalUserIds(canViewValues ? [] : actorId ? [actorId] : [])
    setAppointmentStatuses([])
    setTriageStatuses([])
    setChannels([])
    setPatientQuery('')
    setPolicyNumbers([])
    setConvenioCodes([])
    setAssistanceIds([])
    setReferralOriginIds([])
    setDemandTypeIds([])
    setAppliedFilters(next)
    setRefreshToken((token) => token + 1)
  }

  const tabClass = (tab: SupportCenterDashboardTab) =>
    `py-3 px-1 border-b-2 font-medium text-sm ${
      activeTab === tab
        ? 'border-primary-600 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
    }`

  return (
    <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
            <div className="px-6 pt-6">
              <div className="mb-6">
                <CentralAmparoPageHeader
                  title={moduleInfo?.display_name ?? DEFAULT_MODULE_TITLE}
                  description={
                    moduleInfo?.description ||
                    'Análise de indicadores e Histórico de Atendimentos.'
                  }
                />
              </div>

              <div className="mb-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Data inicial
                    </label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className={DASHBOARD_INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Data final
                    </label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className={DASHBOARD_INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Especialidade
                    </label>
                    {canFilterSpecialization ? (
                      <CheckboxMultiSelect
                        options={specializations.map((spec) => ({
                          value: spec.id,
                          label: spec.name,
                        }))}
                        selected={specializationIds}
                        onChange={setSpecializationIds}
                        placeholder="Todas"
                        disabled={!catalogReady || loadingViewValues}
                        className="w-full"
                      />
                    ) : (
                      <input
                        type="text"
                        value={lockedSpecializationLabel}
                        disabled
                        className={`${DASHBOARD_INPUT_CLASS} opacity-70 cursor-not-allowed`}
                        title="Sem permissão de ver valores: filtro travado na sua especialidade"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Profissional
                    </label>
                    {canFilterProfessional ? (
                      <CheckboxMultiSelect
                        options={professionals.map((professional) => ({
                          value: professional.id,
                          label: professional.name,
                        }))}
                        selected={professionalUserIds}
                        onChange={setProfessionalUserIds}
                        placeholder="Todos"
                        disabled={!catalogReady || loadingViewValues}
                        className="w-full"
                      />
                    ) : (
                      <input
                        type="text"
                        value={profile?.name?.trim() || 'Usuário não identificado'}
                        disabled
                        className={`${DASHBOARD_INPUT_CLASS} opacity-70 cursor-not-allowed`}
                        title="Sem permissão de ver valores: filtro travado na sua agenda"
                      />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status do atendimento
                    </label>
                    <CheckboxMultiSelect
                      options={STATUS_FILTER_OPTIONS}
                      selected={appointmentStatuses}
                      onChange={(selected) =>
                        setAppointmentStatuses(selected as SupportCenterAppointmentStatus[])
                      }
                      placeholder="Todos"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Canal de agendamento
                    </label>
                    <CheckboxMultiSelect
                      options={SUPPORT_CENTER_APPOINTMENT_CHANNEL_FILTER_OPTIONS}
                      selected={channels}
                      onChange={(selected) =>
                        setChannels(selected as SupportCenterAppointmentChannel[])
                      }
                      placeholder="Todos"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Status da triagem
                    </label>
                    <CheckboxMultiSelect
                      options={TRIAGE_STATUS_FILTER_OPTIONS}
                      selected={triageStatuses}
                      onChange={(selected) =>
                        setTriageStatuses(selected as SupportCenterTriageStatusFilter[])
                      }
                      placeholder="Todos"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Origem do encaminhamento
                    </label>
                    <CheckboxMultiSelect
                      options={referralOrigins.map((origin) => ({
                        value: origin.id,
                        label: origin.name,
                      }))}
                      selected={referralOriginIds}
                      onChange={setReferralOriginIds}
                      placeholder="Todas"
                      disabled={!catalogReady}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tipo de demanda
                    </label>
                    <CheckboxMultiSelect
                      options={demandTypes.map((demand) => ({
                        value: demand.id,
                        label: demand.name,
                      }))}
                      selected={demandTypeIds}
                      onChange={setDemandTypeIds}
                      placeholder="Todos"
                      disabled={!catalogReady}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Número da apólice
                    </label>
                    <CheckboxMultiSelect
                      options={policyOptions}
                      selected={policyNumbers}
                      onChange={setPolicyNumbers}
                      placeholder="Todos"
                      disabled={!catalogReady}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Código do Subgrupo
                    </label>
                    <CheckboxMultiSelect
                      options={convenioOptions}
                      selected={convenioCodes}
                      onChange={setConvenioCodes}
                      placeholder="Todos"
                      disabled={!catalogReady}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Cobertura
                    </label>
                    <CheckboxMultiSelect
                      options={coverageOptions}
                      selected={assistanceIds}
                      onChange={setAssistanceIds}
                      placeholder="Todas"
                      disabled={!catalogReady}
                      className="w-full"
                    />
                  </div>
                  <div className="md:col-span-2 xl:col-span-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nome ou CPF do segurado / paciente
                    </label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={patientQuery}
                        onChange={(e) => setPatientQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            applyCurrentFilters()
                          }
                        }}
                        placeholder="Nome ou CPF..."
                        className={DASHBOARD_INPUT_CLASS}
                      />
                      <div className="flex gap-2 shrink-0">
                        <Button
                          variant="solid"
                          onClick={applyCurrentFilters}
                          disabled={filtersLoading || loadingViewValues || !catalogReady}
                        >
                          {filtersLoading ? 'Buscando...' : 'Buscar'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleClearFilters}
                          disabled={filtersLoading || loadingViewValues || !catalogReady}
                        >
                          Limpar filtros
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                {codesError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                    <Text size="sm">{codesError}</Text>
                  </div>
                )}
              </div>
              </div>

              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-8">
                  <button
                    type="button"
                    onClick={() => setActiveTab('volumetria')}
                    className={tabClass('volumetria')}
                  >
                    Indicadores
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('historico')}
                    className={tabClass('historico')}
                  >
                    Histórico de Atendimentos
                  </button>
                </nav>
              </div>
            </div>

            <div className="flex-1 p-6 space-y-6">
              {loadingViewValues || !catalogReady ? (
                <div className="flex justify-center py-16">
                  <LoadingSpinner />
                </div>
              ) : catalogError ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
                  <Text size="sm">{catalogError}</Text>
                </div>
              ) : (
                <>
                  <DashboardVolumetriaTab
                    filters={appliedFilters}
                    specializations={specializations}
                    refreshToken={refreshToken}
                    active={activeTab === 'volumetria'}
                    onLoadingChange={setFiltersLoading}
                  />
                  <DashboardHistoricoTab
                    filters={appliedFilters}
                    refreshToken={refreshToken}
                    active={activeTab === 'historico'}
                    onLoadingChange={setFiltersLoading}
                  />
                </>
              )}
            </div>
          </div>
        </PermissionGuard>
    </main>
  )
}

export default CentralAmparoDashboard
